// NavBar.js - Professional Navigation Component
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaSignOutAlt, FaCog, FaChartBar, FaUserCircle, FaHome } from 'react-icons/fa';
import './NavBar.css';
import wiproLogo from './assets/wipro.png';
import traneLogo from './assets/trane-logo.svg';
import notificationIcon from './assets/notification.svg';
import useNotifications from './hooks/useNotifications';

const NavBar = ({ userName = "John Doe", userEmail = "user@example.com", onLogout, canViewUserAnalytics = false }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();
  const userMenuRef = useRef(null);
  
  // Real-time notifications hook
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(userEmail);

  const isActive = (path) => location.pathname === path;

  // Helper function to format time
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Left: Logos and Brand - matching login page */}
        <div className="navbar-brand">
          <img src={traneLogo} alt="Trane Technologies" className="navbar-logo trane-nav-logo" />
          <span className="navbar-title">AGENT ASSIST</span>
          <span className="powered-by-text">Powered by</span>
          <img src={wiproLogo} alt="Wipro" className="navbar-logo wipro-nav-logo" />
        </div>

        {/* Center: Navigation Links */}
        <div className="navbar-links">
          <Link 
            to="/AgentAssist" 
            className={`nav-link ${isActive('/AgentAssist') ? 'active' : ''}`}
          >
            📊 Dashboard
          </Link>
          <Link 
            to="/analytics" 
            className={`nav-link ${isActive('/analytics') ? 'active' : ''}`}
          >
            📈 Analytics
          </Link>
        </div>

        {/* Right: User Actions */}
        <div className="navbar-actions">
          {/* Real-time Notifications */}
          <div className="notification-container">
            <button 
              className="navbar-icon-btn" 
              title="Notifications"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <img src={notificationIcon} alt="Notifications" style={{ width: '20px', height: '20px' }} />
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            
            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <button className="mark-read-btn" onClick={markAllAsRead}>
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="notification-list">
                  {notifications.length === 0 ? (
                    <div className="notification-empty">
                      <p>No notifications</p>
                    </div>
                  ) : (
                    notifications.map(notification => (
                      <div 
                        key={notification.id} 
                        className={`notification-item ${!notification.read ? 'unread' : ''}`}
                        onClick={() => markAsRead(notification.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="notification-icon">
                          {notification.type === 'ticket_assigned' && '🎫'}
                          {notification.type === 'status_changed' && '✅'}
                          {notification.type === 'alert' && '⚠️'}
                          {!notification.type && '📌'}
                        </div>
                        <div className="notification-content">
                          <p className="notification-text">{notification.message}</p>
                          <span className="notification-time">
                            {formatTimeAgo(notification.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="user-menu-container" ref={userMenuRef}>
            <button
              className={`menu-trigger-btn${showUserMenu ? ' active' : ''}`}
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-expanded={showUserMenu}
              aria-label="Menu"
              title="Menu"
            >
              <span className="menu-trigger-lines" aria-hidden="true">
                <span className="menu-line"></span>
                <span className="menu-line"></span>
                <span className="menu-line"></span>
              </span>
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                {/* Centered Profile Header */}
                <div className="user-dropdown-profile">
                  <div className="user-avatar-xl">{getInitials(userName)}</div>
                  <div className="user-dropdown-name">{userName}</div>
                  <div className="user-dropdown-email">{userEmail}</div>
                </div>

                <div className="dropdown-divider" />

                {/* Navigation Menu Items */}
                <Link
                  to="/"
                  className={`dropdown-menu-item${isActive('/') ? ' dropdown-menu-item-active' : ''}`}
                  onClick={() => setShowUserMenu(false)}
                >
                  <span className="dropdown-menu-icon"><FaHome /></span>
                  <span>Home</span>
                </Link>

                <Link
                  to="/analytics"
                  className={`dropdown-menu-item${isActive('/analytics') ? ' dropdown-menu-item-active' : ''}`}
                  onClick={() => setShowUserMenu(false)}
                >
                  <span className="dropdown-menu-icon"><FaChartBar /></span>
                  <span>Ticket Analytics</span>
                </Link>

                <Link
                  to="/user-analytics"
                  className={`dropdown-menu-item${isActive('/user-analytics') ? ' dropdown-menu-item-active' : ''}`}
                  onClick={() => setShowUserMenu(false)}
                >
                  <span className="dropdown-menu-icon"><FaUserCircle /></span>
                  <span>User Analytics</span>
                </Link>

                <div className="dropdown-divider" />

                <button
                  className="dropdown-menu-item"
                  onClick={() => setShowUserMenu(false)}
                >
                  <span className="dropdown-menu-icon"><FaCog /></span>
                  <span>Settings</span>
                </button>

                <button
                  className="dropdown-menu-item dropdown-menu-danger"
                  onClick={() => { setShowUserMenu(false); onLogout && onLogout(); }}
                >
                  <span className="dropdown-menu-icon"><FaSignOutAlt /></span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
